import React, { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { v4 as uuidV4 } from 'uuid';
import { SIDEBAR_IDS_MAP } from '@jira/admin-pages__fields-page/src/sidebarIds.tsx';

import {
	useSidebars,
	type SidebarTriggerOnClick,
} from '@jira/admin-pages__fields-page/src/SidebarsContext.tsx';
import { Box, Flex, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import Button, { IconButton } from '@atlaskit/button/new';
import Heading from '@atlaskit/heading';
import CrossIcon from '@atlaskit/icon/core/cross';
import { getDocument } from '@atlaskit/browser-apis';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import AmbientBackgroundAbstract from '@atlassian/jira-illustrations/src/ui/adg4/jira/other/components/ambient-background-abstract/index.tsx';
import CoinsFlat from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/other/components/coins-flat/index.tsx';
import { useRouterActions } from '@atlassian/react-resource-router';
import { useFlagsService } from '@atlassian/jira-flags/src/services/index.tsx';
import { useTrigger } from '@atlassian/post-office-context';
import getLocation from '@atlassian/jira-common-location/src/index.tsx';
import type { ContentProps } from '../types.tsx';
import messages from '../messages.tsx';

const EVENT_ID = 'jira-cmp-currency-field-changeboarding-trigger-id';
const emitMessageEvent = (eventDetails: { action: 'show-me' | 'dismiss' }) => {
	const event = new CustomEvent(EVENT_ID, {
		detail: eventDetails,
	});

	getDocument()?.dispatchEvent(event);
};

const styles = cssMap({
	container: {
		width: '300px',
		backgroundColor: token('elevation.surface.overlay'),
		boxShadow: token('elevation.shadow.overflow'),
		borderRadius: token('border.radius.050'),
	},
	content: {
		paddingTop: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
	},
	illustrationBackground: {
		position: 'relative',
	},
	illustrationCoins: {
		position: 'absolute',
		transform: 'translate(-50%, -50%)',
		top: '50%',
		left: '50%',
		zIndex: 1,
	},
});

const CmpCurrencyFormattingFlag = () => {
	const { push } = useRouterActions();
	const { showFlag, dismissFlag, hasFlag } = useFlagsService();
	const { formatMessage } = useIntl();

	const { isActive } = useTrigger(EVENT_ID);

	const handleShowMe = useCallback(() => {
		emitMessageEvent({ action: 'show-me' });
		push('/jira/settings/issues/fields?sidebarId=create&sidebarView=currency');
		dismissFlag(EVENT_ID);
	}, [dismissFlag, push]);

	const handleDismiss = useCallback(() => {
		dismissFlag(EVENT_ID);
	}, [dismissFlag]);

	const location = getLocation();
	const onFieldsPage = location && location.pathname.includes('/jira/settings/issues/fields');

	const RedirectButton = useMemo(
		() => (
			<Button onClick={handleShowMe} appearance="primary">
				{formatMessage(messages.showMe)}
			</Button>
		),
		[formatMessage, handleShowMe],
	);

	const ShowMeButton = onFieldsPage ? CreateFieldButton() : RedirectButton;

	useEffect(() => {
		if (!hasFlag?.(EVENT_ID) && isActive && fg('frontier-currency-field-d3'))
			showFlag({
				id: EVENT_ID,
				messageId: 'cmp-currency-field-changeboarding.ui.components.show-flag.flag.show-flag',
				messageType: 'engagement',
				isAutoDismiss: false,
				render: () => <FlagContent onDismiss={handleDismiss}>{ShowMeButton}</FlagContent>,
			});
	}, [ShowMeButton, handleDismiss, handleShowMe, hasFlag, isActive, showFlag]);
	return null;
};

export const FlagContent = ({ onDismiss, children }: ContentProps) => {
	const { formatMessage } = useIntl();
	return (
		<Stack xcss={styles.container}>
			<Inline alignInline="end">
				<IconButton
					label={formatMessage(messages.dismiss)}
					icon={CrossIcon}
					appearance="subtle"
					onClick={onDismiss}
				/>
			</Inline>
			<Flex justifyContent="center" alignItems="center" xcss={styles.illustrationBackground}>
				<AmbientBackgroundAbstract height={126} width={300} alt="" />
				<Box xcss={styles.illustrationCoins}>
					<CoinsFlat height={126} width={200} alt="" />
				</Box>
			</Flex>
			<Stack space="space.200" xcss={styles.content}>
				<Stack space="space.100">
					<Heading size="small">{formatMessage(messages.heading)}</Heading>
					<Text>
						{formatMessage(messages.body, {
							b: (...chunks: ReactNode[]) => <Text as="strong">{chunks}</Text>,
						})}
					</Text>
				</Stack>
				<Inline space="space.100">
					{children}
					<Button onClick={onDismiss}>{formatMessage(messages.dismiss)}</Button>
				</Inline>
			</Stack>
		</Stack>
	);
};

const CreateFieldButton = () => {
	const { formatMessage } = useIntl();
	const { dismissFlag } = useFlagsService();
	const { openSidebar: openCreateSidebar, SidebarTrigger } = useSidebars(SIDEBAR_IDS_MAP.CREATE);
	const [createSessionId, setCreateSessionId] = useState(uuidV4);

	const handleCreateField: SidebarTriggerOnClick = useCallback(() => {
		dismissFlag(EVENT_ID);
		emitMessageEvent({ action: 'show-me' });
		openCreateSidebar({
			createSessionId,
		});
		setCreateSessionId(uuidV4());
	}, [createSessionId, dismissFlag, openCreateSidebar]);

	return (
		<SidebarTrigger
			onClick={handleCreateField}
			entryPointProps={{
				createSessionId,
				sidebarView: 'currency',
			}}
		>
			{({ ref, onClick }) => {
				return (
					<Button ref={ref} appearance="primary" onClick={onClick}>
						{formatMessage(messages.showMe)}
					</Button>
				);
			}}
		</SidebarTrigger>
	);
};

export default CmpCurrencyFormattingFlag;
